(() => {
  if (window.__srLandingScriptLoaded) return;
  window.__srLandingScriptLoaded = true;

  const selectAll = (root, selector) => Array.from(root.querySelectorAll(selector));

  const getCartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const getCartAddUrl = () => (window.routes && window.routes.cart_add_url) || '/cart/add';
  const getPassSelectionUrl = () => {
    const rootUrl = (window.routes && window.routes.root_url) || '/';
    return `${rootUrl.replace(/\/$/, '')}/pages/passes`;
  };

  const getCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return null;
    if (typeof cartDrawer.renderContents !== 'function' || typeof cartDrawer.open !== 'function') return null;
    return cartDrawer;
  };

  const appendCartDrawerSections = (formData, cartDrawer) => {
    if (!cartDrawer) return;

    const sections = cartDrawer
      .getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    if (sections) formData.append('sections', sections);
    formData.append('sections_url', window.location.pathname);
  };

  const updateCartNav = (cart) => {
    if (!cart || typeof cart.item_count === 'undefined') return;

    const count = Number(cart.item_count) || 0;
    const label = count > 0 ? 'Current Passes' : 'Get Your Pass';
    const href = count > 0 ? getCartUrl() : getPassSelectionUrl();

    selectAll(document, '[data-sr-pass-cart]').forEach((link) => {
      link.setAttribute('href', href);
    });

    selectAll(document, '[data-sr-cart-label]').forEach((node) => {
      node.textContent = label;
    });

    selectAll(document, '[data-sr-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.classList.toggle('is-empty', count === 0);
      node.setAttribute('aria-label', `${count} items in cart`);
    });
  };

  const fetchCart = async () => {
    const response = await fetch(`${getCartUrl()}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to fetch cart.');
    return response.json();
  };

  const initCartUpdateBridge = () => {
    if (window.__srCartUpdateBridgeLoaded) return;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    window.__srCartUpdateBridgeLoaded = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
      if (event.cartData && typeof event.cartData.item_count !== 'undefined') {
        updateCartNav(event.cartData);
        document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart: event.cartData } }));
        return;
      }

      fetchCart()
        .then((cart) => {
          updateCartNav(cart);
          document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));
        })
        .catch(() => {});
    });
  };

  const initCartForms = (root) => {
    const forms = selectAll(root, '[data-sr-product-form]');
    if (!forms.length) return;

    forms.forEach((form) => {
      if (form.dataset.srProductFormInit === 'true') return;
      form.dataset.srProductFormInit = 'true';

      const submitButton = form.querySelector('[type="submit"]');
      const labelNode = form.querySelector('[data-sr-product-form-label]');
      const statusScope = form.closest('.sr-pass-ticket') || form.closest('.sr-pass-card') || form;
      const statusNode = statusScope.querySelector('[data-sr-product-form-status]');
      const initialLabel = labelNode ? labelNode.textContent : '';
      const initialStatus = statusNode ? statusNode.textContent : '';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!submitButton || submitButton.disabled) return;

        submitButton.disabled = true;
        form.dataset.srProductFormState = 'loading';
        if (labelNode) labelNode.textContent = 'Adding...';
        if (statusNode) statusNode.textContent = '';

        try {
          const cartDrawer = getCartDrawer();
          const formData = new FormData(form);
          appendCartDrawerSections(formData, cartDrawer);

          const response = await fetch(getCartAddUrl(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });

          if (!response.ok) {
            let errorMessage = 'Could not add this pass.';
            try {
              const error = await response.json();
              errorMessage = error.description || error.message || errorMessage;
            } catch (_error) {
              errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
          }

          const addResponse = await response.json();
          const cart = await fetchCart();
          updateCartNav(cart);
          document.dispatchEvent(new CustomEvent('sr:cart-updated', { detail: { cart } }));

          if (cartDrawer && addResponse.sections) {
            cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
            try {
              cartDrawer.renderContents(addResponse);
            } catch (_error) {
              cartDrawer.open(submitButton);
            }
          }

          form.dataset.srProductFormState = 'success';
          if (labelNode) labelNode.textContent = 'Added';
          if (statusNode) statusNode.textContent = 'Added to cart';

          window.setTimeout(() => {
            form.dataset.srProductFormState = '';
            if (labelNode) labelNode.textContent = initialLabel;
            if (statusNode) statusNode.textContent = initialStatus;
            submitButton.disabled = false;
          }, 2200);
        } catch (error) {
          form.dataset.srProductFormState = 'error';
          if (labelNode) labelNode.textContent = initialLabel || 'Try again';
          if (statusNode) statusNode.textContent = error.message;
          submitButton.disabled = false;
        }
      });
    });
  };

  const initPassGrid = (root) => {
    const grid = root.querySelector('[data-sr-pass-grid]');
    if (!grid) return;

    const cells = selectAll(grid, '.sr-passes__cell');
    const cards = selectAll(grid, '[data-sr-pass-card]');
    const chips = selectAll(root, '[data-sr-pass-filter]');
    const status = root.querySelector('[data-sr-pass-status]');
    const filterIds = chips.map((chip) => chip.getAttribute('data-sr-pass-filter'));

    const revealCard = (card) => card.classList.add('is-revealed');

    const applyFilter = (filterId, { announce = true } = {}) => {
      const id = filterIds.includes(filterId) ? filterId : 'all';
      let shown = 0;
      let label = '';

      chips.forEach((chip) => {
        const isActive = chip.getAttribute('data-sr-pass-filter') === id;
        chip.classList.toggle('is-active', isActive);
        chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) label = chip.textContent.trim();
      });

      cells.forEach((cell) => {
        const card = cell.querySelector('[data-sr-pass-card]');
        const matches =
          id === 'all' ||
          (card &&
            (id === 'premium'
              ? card.getAttribute('data-tier') === 'premium'
              : card.getAttribute('data-audience') === id));
        cell.hidden = !matches;
        if (matches) {
          shown += 1;
          if (card) revealCard(card);
        }
      });

      grid.classList.toggle('is-all', id === 'all');
      grid.scrollLeft = 0;

      if (status && announce) {
        if (id === 'all') {
          status.textContent = `Showing all ${shown} passes`;
        } else if (id === 'premium') {
          status.textContent = `Showing ${shown} Premium passes`;
        } else {
          status.textContent = `Showing ${shown} passes for ${label}`;
        }
      }
    };

    const setFilterHash = (id) => {
      const nextHash = id && id !== 'all' ? `#${id}` : '';
      if (window.location.hash === nextHash) return;
      history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const id = chip.getAttribute('data-sr-pass-filter');
        applyFilter(id);
        setFilterHash(id);
      });
    });

    const filters = root.querySelector('.sr-passes__filters');
    // Chips live in a horizontal scroll strip, so scroll the strip itself: its
    // scroll-margin is measured against the window, a chip's is not.
    const scrollToChip = () => {
      if (!filters) return;
      filters.scrollIntoView({ block: 'start' });
      const active = filters.querySelector('.is-active');
      if (!active) return;
      const offset = active.offsetLeft - filters.clientWidth / 2 + active.offsetWidth / 2;
      filters.scrollTo({ left: Math.max(0, offset) });
    };

    const filterFromHash = () => {
      const hashId = window.location.hash.replace(/^#/, '');
      if (hashId && filterIds.includes(hashId) && hashId !== 'all') {
        applyFilter(hashId, { announce: false });
        return hashId;
      }
      return null;
    };

    // The browser jumps to the hash before fonts and images settle, so re-align
    // once the page has loaded. Covers audience chips and the Premium filter.
    const afterLoad = (fn) => {
      const run = () => requestAnimationFrame(fn);
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    };

    const initialHash = filterFromHash();
    if (initialHash) {
      afterLoad(scrollToChip);
    } else {
      applyFilter('all', { announce: false });
      const rawHash = window.location.hash.replace(/^#/, '');
      const target = rawHash ? grid.querySelector(`#${CSS.escape(rawHash)}`) : null;
      if (target) afterLoad(() => target.scrollIntoView({ block: 'start' }));
    }

    // Reveal cards as the grid scrolls into view; stagger is handled in CSS via --sr-i.
    let revealObserver = null;
    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealCard(entry.target);
            obs.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
      );
      cards.forEach((card) => revealObserver.observe(card));
    } else {
      cards.forEach(revealCard);
    }

    const onHashChange = () => {
      if (filterFromHash()) requestAnimationFrame(scrollToChip);
    };

    window.addEventListener('hashchange', onHashChange);
    root.addEventListener(
      'sr:cleanup',
      () => {
        window.removeEventListener('hashchange', onHashChange);
        if (revealObserver) revealObserver.disconnect();
      },
      { once: true }
    );
  };

  const initFaq = (root) => {
    const list = root.querySelector('[data-faq-list]');
    const items = selectAll(root, '[data-faq-item]');
    if (!items.length) return;

    const bar = list ? list.querySelector('[data-faq-bar]') : null;
    let activeItem = null;
    let trackFrameId = 0;
    let trackEndAt = 0;

    const positionBar = () => {
      if (!bar) return;
      if (activeItem) {
        bar.style.top = `${activeItem.offsetTop}px`;
        bar.style.height = `${activeItem.offsetHeight}px`;
        bar.classList.add('is-active');
      } else {
        bar.classList.remove('is-active');
      }
    };

    const trackBar = () => {
      positionBar();
      if (performance.now() < trackEndAt) {
        trackFrameId = requestAnimationFrame(trackBar);
      }
    };

    const startTracking = (durationMs = 500) => {
      cancelAnimationFrame(trackFrameId);
      trackEndAt = performance.now() + durationMs;
      trackFrameId = requestAnimationFrame(trackBar);
    };

    const setOpenState = (item, isOpen) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      const panel = item.querySelector('[data-faq-panel]');

      item.classList.toggle('is-open', isOpen);
      if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (panel) {
        if (isOpen) panel.removeAttribute('inert');
        else panel.setAttribute('inert', '');
      }
    };

    const openExclusive = (target) => {
      items.forEach((item) => setOpenState(item, item === target));
      activeItem = target;
      startTracking();
    };

    const close = (item) => {
      setOpenState(item, false);
      if (activeItem === item) activeItem = null;
      startTracking();
    };

    let initiallyOpen =
      items.find((item) => item.classList.contains('is-open')) || items[0];
    items.forEach((item) => setOpenState(item, item === initiallyOpen));
    activeItem = initiallyOpen || null;

    if (bar && activeItem) {
      const prevTransition = bar.style.transition;
      bar.style.transition = 'none';
      positionBar();
      requestAnimationFrame(() => {
        bar.style.transition = prevTransition;
      });
    }

    items.forEach((item) => {
      const trigger = item.querySelector('[data-faq-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', () => {
        const currentlyOpen = item.classList.contains('is-open');
        if (currentlyOpen) close(item);
        else openExclusive(item);
      });
    });

    const onResize = () => positionBar();
    window.addEventListener('resize', onResize);
    root.addEventListener(
      'sr:cleanup',
      () => {
        window.removeEventListener('resize', onResize);
        cancelAnimationFrame(trackFrameId);
      },
      { once: true }
    );
  };

  const initHomeNav = (root) => {
    const nav = root.querySelector('[data-home-nav]');
    if (!nav) return;

    const mobilePanel = root.querySelector('[data-home-nav-panel]');
    const openButton = root.querySelector('[data-home-nav-open]');
    const closeButton = root.querySelector('[data-home-nav-close]');

    const updateScrolled = () => {
      nav.classList.toggle('is-scrolled', window.scrollY > 40);
    };

    const initAnnounce = () => {
      const bar = nav.querySelector('[data-sr-announce]');
      if (!bar) return;

      const deadlineRaw = bar.dataset.annDeadline;
      const deadline = deadlineRaw ? new Date(deadlineRaw).getTime() : NaN;

      const removeBar = () => {
        bar.setAttribute('hidden', '');
        nav.classList.remove('has-announce');
      };

      if (!Number.isFinite(deadline)) return;

      const dEl = bar.querySelector('[data-ann-days]');
      const hEl = bar.querySelector('[data-ann-hours]');
      const mEl = bar.querySelector('[data-ann-mins]');
      const sEl = bar.querySelector('[data-ann-secs]');
      if (!dEl || !hEl || !mEl || !sEl) return;

      const pad = (n) => (n < 10 ? '0' + n : '' + n);

      let intervalId = null;
      const tick = () => {
        const diff = deadline - Date.now();
        if (diff <= 0) {
          dEl.textContent = hEl.textContent = mEl.textContent = sEl.textContent = '00';
          removeBar();
          if (intervalId) clearInterval(intervalId);
          return;
        }
        const total = Math.floor(diff / 1000);
        const days  = Math.floor(total / 86400);
        const hours = Math.floor((total % 86400) / 3600);
        const mins  = Math.floor((total % 3600) / 60);
        const secs  = total % 60;

        const nextDays = pad(days);
        const nextHours = pad(hours);
        const nextMins = pad(mins);
        const nextSecs = pad(secs);

        if (dEl.textContent !== nextDays) dEl.textContent = nextDays;
        if (hEl.textContent !== nextHours) hEl.textContent = nextHours;
        if (mEl.textContent !== nextMins) mEl.textContent = nextMins;
        if (sEl.textContent !== nextSecs) {
          sEl.textContent = nextSecs;
          sEl.classList.remove('is-tick');
          // force reflow so the animation can replay
          // eslint-disable-next-line no-unused-expressions
          sEl.offsetWidth;
          sEl.classList.add('is-tick');
        }
      };

      tick();
      intervalId = setInterval(tick, 1000);

      root.addEventListener(
        'sr:cleanup',
        () => { if (intervalId) clearInterval(intervalId); },
        { once: true }
      );
    };

    initAnnounce();

    const closeMobile = () => {
      if (!mobilePanel) return;
      mobilePanel.hidden = true;
      document.body.style.overflow = '';
    };

    const openMobile = () => {
      if (!mobilePanel) return;
      mobilePanel.hidden = false;
      document.body.style.overflow = 'hidden';
    };

    const mobileLinks = selectAll(root, '.sr-home-nav-mobile a');

    window.addEventListener('scroll', updateScrolled, { passive: true });
    updateScrolled();

    if (openButton) openButton.addEventListener('click', openMobile);
    if (closeButton) closeButton.addEventListener('click', closeMobile);
    mobileLinks.forEach((link) => link.addEventListener('click', closeMobile));

    selectAll(root, '[data-sr-pass-cart]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const cartDrawer = getCartDrawer();
        const countNode = link.querySelector('[data-sr-cart-count]');
        const count = countNode ? Number(countNode.textContent) || 0 : 0;

        if (!cartDrawer || count < 1) return;

        event.preventDefault();
        closeMobile();
        cartDrawer.open(link);
      });
    });

    root.addEventListener(
      'sr:cleanup',
      () => {
        window.removeEventListener('scroll', updateScrolled);
        closeMobile();
      },
      { once: true }
    );
  };

  const initAgenda = (root) => {
    const agenda = root.querySelector('[data-agenda]');
    if (!agenda) return;

    const dayButtons = selectAll(agenda, '[data-agenda-day]');
    if (!dayButtons.length) return;

    const dayPanels = selectAll(agenda, '[data-agenda-day-panel]');
    const stageRows = selectAll(agenda, '[data-agenda-stage-row]');
    const allStageButtons = selectAll(agenda, '[data-agenda-stage]');

    const setActiveStage = (dayHandle, stageHandle) => {
      const dayPanel = dayPanels.find(
        (p) => p.dataset.agendaDayPanel === dayHandle
      );
      if (!dayPanel) return;

      const stagePanels = selectAll(dayPanel, '[data-agenda-stage-panel]');
      let resolved = stageHandle;
      const exists = stagePanels.some(
        (sp) => sp.dataset.agendaStagePanel === resolved
      );
      if (!exists && stagePanels.length) {
        resolved = stagePanels[0].dataset.agendaStagePanel;
      }

      stagePanels.forEach((sp) => {
        sp.dataset.active =
          sp.dataset.agendaStagePanel === resolved ? 'true' : 'false';
      });

      allStageButtons
        .filter((btn) => btn.dataset.agendaStageDay === dayHandle)
        .forEach((btn) => {
          btn.setAttribute(
            'aria-selected',
            btn.dataset.agendaStage === resolved ? 'true' : 'false'
          );
        });
    };

    const setActiveDay = (dayHandle) => {
      dayButtons.forEach((btn) => {
        btn.setAttribute(
          'aria-pressed',
          btn.dataset.agendaDay === dayHandle ? 'true' : 'false'
        );
      });

      dayPanels.forEach((panel) => {
        panel.dataset.active =
          panel.dataset.agendaDayPanel === dayHandle ? 'true' : 'false';
      });

      stageRows.forEach((row) => {
        row.hidden = row.dataset.agendaStageRow !== dayHandle;
      });

      const activePanel = dayPanels.find(
        (p) => p.dataset.agendaDayPanel === dayHandle
      );
      if (activePanel) {
        const firstStage = activePanel.querySelector(
          '[data-agenda-stage-panel]'
        );
        if (firstStage) {
          setActiveStage(dayHandle, firstStage.dataset.agendaStagePanel);
        }
      }
    };

    const sticky = agenda.querySelector('.sr-agenda__sticky');

    // Bring the start of the newly shown panel just below the sticky tab bar.
    const scrollToPanelTop = () => {
      const activePanel = dayPanels.find((p) => p.dataset.active === 'true');
      if (!activePanel) return;

      const stickyOffset = sticky
        ? (parseFloat(getComputedStyle(sticky).top) || 0) + sticky.offsetHeight
        : 0;
      const target =
        activePanel.getBoundingClientRect().top + window.scrollY - stickyOffset;

      if (window.scrollY <= target + 1) return;

      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      window.scrollTo({
        top: Math.max(target, 0),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    };

    dayButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        setActiveDay(btn.dataset.agendaDay);
        syncStageEdges();
        scrollToPanelTop();
      });
    });

    // On mobile the stage row scrolls sideways; drop its edge fade at the end.
    const stageLists = selectAll(agenda, '.sr-agenda__stage-tabs');
    const syncStageEdge = (list) => {
      list.toggleAttribute(
        'data-scroll-end',
        list.clientWidth > 0 &&
          list.scrollLeft + list.clientWidth >= list.scrollWidth - 2
      );
    };
    const syncStageEdges = () => stageLists.forEach(syncStageEdge);
    stageLists.forEach((list) => {
      list.addEventListener('scroll', () => syncStageEdge(list), {
        passive: true,
      });
    });
    window.addEventListener('resize', syncStageEdges);
    syncStageEdges();

    allStageButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        setActiveStage(btn.dataset.agendaStageDay, btn.dataset.agendaStage);
        const list = btn.parentElement;
        if (list.scrollWidth > list.clientWidth) {
          const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches;
          list.scrollTo({
            left:
              list.scrollLeft +
              btn.getBoundingClientRect().left -
              list.getBoundingClientRect().left -
              (list.clientWidth - btn.offsetWidth) / 2,
            behavior: reduceMotion ? 'auto' : 'smooth',
          });
        }
        scrollToPanelTop();
      });
    });

    selectAll(agenda, '[data-agenda-empty-switch]').forEach((btn) => {
      btn.addEventListener('click', () => {
        setActiveStage(
          btn.dataset.agendaEmptyDay,
          btn.dataset.agendaEmptySwitch
        );
        scrollToPanelTop();
      });
    });
  };

  const initSpeakerDays = () => {
    const host = document.querySelector('[data-sr-speaker-days]');
    if (!host) return;

    const rail = host.querySelector('[data-sr-day-rail]');
    const buttons = selectAll(host, '[data-sr-day]');
    if (!rail || !buttons.length) return;

    const panels = selectAll(document, '[data-sr-speaker-day]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const keysFor = (panel) =>
      (panel.getAttribute('data-sr-speaker-day') || '')
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);

    // Replays the staggered card reveal so a day switch re-deals the face wall.
    const redeal = (panel) => {
      if (reduceMotion) return;
      const cards = selectAll(panel, '.sr-speaker-card.is-revealed');
      if (!cards.length) return;

      cards.forEach((card) => card.classList.remove('is-revealed'));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          cards.forEach((card) => card.classList.add('is-revealed'));
        });
      });
    };

    const setDay = (key, options) => {
      const opts = options || {};
      let resolved = key;
      if (!buttons.some((btn) => btn.dataset.srDay === resolved)) {
        resolved = buttons[0].dataset.srDay;
      }

      buttons.forEach((btn) => {
        const on = btn.dataset.srDay === resolved;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) rail.style.setProperty('--sr-day-active', btn.dataset.srDayIndex || '0');
      });

      let seenFirst = false;
      panels.forEach((panel) => {
        const on = keysFor(panel).indexOf(resolved) !== -1;
        const wasVisible = !panel.hidden;
        panel.hidden = !on;

        if (on && !seenFirst) {
          panel.setAttribute('data-sr-day-first', '');
          seenFirst = true;
        } else {
          panel.removeAttribute('data-sr-day-first');
        }

        if (on && !wasVisible && opts.animate !== false) redeal(panel);
      });

      host.dataset.srActiveDay = resolved;

      if (opts.updateHash !== false && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '#' + resolved);
      }
    };

    buttons.forEach((btn) => {
      if (btn.dataset.srDayBound === 'true') return;
      btn.dataset.srDayBound = 'true';
      btn.addEventListener('click', () => setDay(btn.dataset.srDay));
    });

    // Theme editor: picking a speakers section that sits on a hidden day
    // should switch to that day rather than scroll to nothing.
    host.srSetDay = setDay;

    if (document.documentElement.dataset.srDaySelectBound !== 'true') {
      document.documentElement.dataset.srDaySelectBound = 'true';
      document.addEventListener('shopify:section:select', (event) => {
        const days = document.querySelector('[data-sr-speaker-days]');
        if (!days || typeof days.srSetDay !== 'function') return;

        const panel = event.target.querySelector('[data-sr-speaker-day]');
        if (!panel || !panel.hidden) return;

        const keys = (panel.getAttribute('data-sr-speaker-day') || '')
          .trim()
          .toLowerCase()
          .split(/\s+/)
          .filter(Boolean);
        if (keys.length) days.srSetDay(keys[0]);
      });
    }

    const bar = host.querySelector('[data-sr-day-bar]');
    const dock = host.querySelector('[data-sr-day-dock]');

    if (
      bar &&
      dock &&
      bar.classList.contains('sr-speaker-days__bar--dockable') &&
      'IntersectionObserver' in window &&
      host.dataset.srDayDockInit !== 'true'
    ) {
      host.dataset.srDayDockInit = 'true';
      let footerVisible = false;

      const setDocked = (docked) => {
        if (docked === bar.classList.contains('is-docked')) return;

        if (!docked) {
          bar.classList.remove('is-docked');
          bar.removeAttribute('data-sr-day-bar-hidden');
          dock.style.height = '';
          return;
        }

        dock.style.height = bar.offsetHeight + 'px';
        bar.setAttribute('data-sr-day-bar-hidden', '');
        bar.classList.add('is-docked');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (bar.classList.contains('is-docked') && !footerVisible) {
              bar.removeAttribute('data-sr-day-bar-hidden');
            }
          });
        });
      };

      const navClearancePx = () => {
        const nav = document.querySelector('[data-home-nav]');
        if (!nav || !nav.classList.contains('has-announce')) return 64;
        const announce = nav.querySelector('[data-sr-announce]:not([hidden])');
        const announceH = announce ? announce.getBoundingClientRect().height : 0;
        return Math.round(64 + announceH);
      };

      const dockObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setDocked(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { rootMargin: `-${navClearancePx()}px 0px 0px 0px`, threshold: 0 }
      );
      dockObserver.observe(dock);

      const footer = document.querySelector('.sr-homepage-footer');
      if (footer) {
        const footerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              footerVisible = entry.isIntersecting;
              if (!bar.classList.contains('is-docked')) return;
              if (footerVisible) bar.setAttribute('data-sr-day-bar-hidden', '');
              else bar.removeAttribute('data-sr-day-bar-hidden');
            });
          },
          { threshold: 0 }
        );
        footerObserver.observe(footer);
      }
    }

    const hashKey = (window.location.hash || '').replace('#', '').trim().toLowerCase();
    const initial = buttons.some((btn) => btn.dataset.srDay === hashKey)
      ? hashKey
      : host.dataset.srActiveDay || host.dataset.srDefaultDay || buttons[0].dataset.srDay;

    setDay(initial, { updateHash: false, animate: false });
    document.documentElement.setAttribute('data-sr-days-ready', 'true');
  };

  const initRevealOnView = (root) => {
    const targets = selectAll(
      root,
      '[data-sr-stack], [data-sr-reveal-on-view]'
    );
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach((target) =>
        target.setAttribute('data-sr-in-view', '')
      );
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.setAttribute('data-sr-in-view', '');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -80px 0px', threshold: 0.05 }
    );

    targets.forEach((target) => observer.observe(target));

    root.addEventListener('sr:cleanup', () => observer.disconnect(), {
      once: true,
    });
  };

  const initHeroTracks = (root) => {
    const rotator = root.querySelector('[data-sr-hero-tracks]');
    if (!rotator || rotator.dataset.srHeroTracksInit === 'true') return;

    const tracks = selectAll(rotator, '[data-sr-hero-track]');
    if (!tracks.length) return;

    const descriptions = selectAll(
      rotator,
      '[data-sr-hero-description]'
    );

    rotator.dataset.srHeroTracksInit = 'true';

    const configuredInterval = Number(rotator.dataset.rotationInterval);
    const rotationInterval = Number.isFinite(configuredInterval)
      ? configuredInterval
      : 5000;
    let activeIndex = 0;
    let rotationTimer;

    // Replay the lighter swap animation. Toggling `hidden` alone would
    // restart the heavy intro rise (srHeroTitleRise); forcing a reflow with
    // the class applied makes the element animate via `.is-swapping` instead.
    const replaySwap = (el) => {
      el.classList.add('is-swapping');
      el.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.style.animation = '';
    };

    // `animate` is false only for the initial render, where the static title
    // lines ("The Future of") and first track play the intro rise once.
    const showTrack = (nextIndex, animate = true) => {
      activeIndex = (nextIndex + tracks.length) % tracks.length;
      tracks.forEach((track, index) => {
        const isActive = index === activeIndex;
        track.hidden = !isActive;
        if (isActive && animate) replaySwap(track);
      });
      descriptions.forEach((description, index) => {
        const isActive = index === activeIndex;
        description.hidden = !isActive;
        if (isActive && animate) replaySwap(description);
      });
    };

    const stopRotation = () => {
      if (!rotationTimer) return;
      window.clearInterval(rotationTimer);
      rotationTimer = undefined;
    };

    const startRotation = () => {
      stopRotation();
      if (tracks.length < 2) return;
      rotationTimer = window.setInterval(() => {
        showTrack(activeIndex + 1);
      }, rotationInterval);
    };

    const handleBlockSelect = (event) => {
      const selectedTrack = event.target.closest('[data-sr-hero-track]');
      if (!selectedTrack || !rotator.contains(selectedTrack)) return;

      stopRotation();
      showTrack(tracks.indexOf(selectedTrack));
    };

    const handleBlockDeselect = (event) => {
      const deselectedTrack = event.target.closest('[data-sr-hero-track]');
      if (!deselectedTrack || !rotator.contains(deselectedTrack)) return;
      startRotation();
    };

    root.addEventListener('shopify:block:select', handleBlockSelect);
    root.addEventListener('shopify:block:deselect', handleBlockDeselect);
    root.addEventListener(
      'sr:cleanup',
      () => {
        stopRotation();
        root.removeEventListener('shopify:block:select', handleBlockSelect);
        root.removeEventListener('shopify:block:deselect', handleBlockDeselect);
        delete rotator.dataset.srHeroTracksInit;
      },
      { once: true }
    );

    showTrack(0, false);
    startRotation();
  };

  // Hero headline word slot: "Your next ______ is already registered."
  // Words are stacked in a masked slot and cycled top to bottom, with the slot
  // width tweening to each incoming word so the sentence stays tight.
  const initHeroWordSlot = (root) => {
    const slot = root.querySelector('[data-sr-word-slot]');
    if (!slot || slot.dataset.srWordSlotInit === 'true') return;

    const words = selectAll(slot, '[data-sr-word]');
    if (!words.length) return;

    slot.dataset.srWordSlotInit = 'true';

    const SLIDE_MS = 620;
    const configuredSeconds = Number(slot.dataset.rotationInterval);
    const dwellMs =
      Number.isFinite(configuredSeconds) && configuredSeconds > 0
        ? configuredSeconds * 1000
        : 2500;

    let activeIndex = 0;
    let rotationTimer;
    let parkTimer;

    const measure = (el) => el.getBoundingClientRect().width;

    // Park a word above the mask without animating it back up through the slot.
    const park = (el) => {
      el.style.transition = 'none';
      el.style.transform = 'translateY(-104%)';
      el.style.opacity = '0';
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.style.transition = '';
    };

    const syncWidth = (animate) => {
      if (!animate) slot.style.transition = 'none';
      slot.style.width = `${measure(words[activeIndex])}px`;
      if (!animate) {
        // eslint-disable-next-line no-unused-expressions
        slot.offsetWidth;
        slot.style.transition = '';
      }
    };

    const showWord = (nextIndex) => {
      const next = (nextIndex + words.length) % words.length;
      if (next === activeIndex) return;

      const outgoing = words[activeIndex];
      const incoming = words[next];

      outgoing.style.transform = 'translateY(104%)';
      outgoing.style.opacity = '0';
      incoming.style.transform = 'translateY(0)';
      incoming.style.opacity = '1';

      activeIndex = next;
      syncWidth(true);

      window.clearTimeout(parkTimer);
      parkTimer = window.setTimeout(() => park(outgoing), SLIDE_MS + 60);
    };

    const stopRotation = () => {
      if (!rotationTimer) return;
      window.clearInterval(rotationTimer);
      rotationTimer = undefined;
    };

    const startRotation = () => {
      stopRotation();
      if (words.length < 2) return;
      rotationTimer = window.setInterval(() => {
        showWord(activeIndex + 1);
      }, dwellMs);
    };

    const handleVisibility = () => {
      if (document.hidden) stopRotation();
      else startRotation();
    };

    let resizeTimer;
    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => syncWidth(false), 120);
    };

    const activate = () => {
      slot.setAttribute('data-sr-word-slot-ready', '');
      words.forEach((word, index) => {
        if (index === activeIndex) {
          word.style.transform = 'translateY(0)';
          word.style.opacity = '1';
        }
      });
      syncWidth(false);
      startRotation();
    };

    // Measure only once the display face is in, or the slot locks to a
    // fallback-font width and every word sits slightly wrong.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(activate).catch(activate);
    } else {
      activate();
    }

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('resize', handleResize);

    root.addEventListener(
      'sr:cleanup',
      () => {
        stopRotation();
        window.clearTimeout(parkTimer);
        window.clearTimeout(resizeTimer);
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('resize', handleResize);
        delete slot.dataset.srWordSlotInit;
      },
      { once: true }
    );
  };

  const initSection = (root) => {
    if (!root || root.dataset.srLandingInit === 'true') return;

    root.dataset.srLandingInit = 'true';
    initHeroTracks(root);
    initHeroWordSlot(root);
    initPassGrid(root);
    initFaq(root);
    initHomeNav(root);
    initCartForms(root);
    initAgenda(root);
    initRevealOnView(root);
  };

  const initAll = (scope = document) => {
    selectAll(scope, '[data-sr-landing]').forEach(initSection);
    initSpeakerDays();
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('DOMContentLoaded', initCartUpdateBridge);
  if (document.readyState !== 'loading') {
    initAll();
    initCartUpdateBridge();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    selectAll(event.target, '[data-sr-landing]').forEach((root) => {
      root.dispatchEvent(new Event('sr:cleanup'));
      delete root.dataset.srLandingInit;
    });
  });
})();
